import React from 'react';

import { Hero } from '@/administration/pages/Journey/common/molecules/JourneyHero';
import { useCurrentUserAccess } from '@/store/currentUser/userCurrentUserAccess';
import { StartProgressOverview } from '@/features/course/learner/atoms/StartProgressOverview';
import { StartAdminButtonGroup } from '@/features/course/learner/atoms/StartAdminButtonGroup';
import { StartDescription } from '@/features/course/learner/atoms/StartDescription';
import { StartLayout } from '@/features/course/learner/atoms/StartLayout/StartLayout';
import { Button } from '@/ui/Button';
import { UseSurveyViewerHook } from '@/features/course/learner/hooks/useSurveyViewer';
import { LearningJourneyItem } from '@/store/v2/journeys/journey';
import { StartUpNext } from '@/features/course/learner/atoms/StartUpNext/StartUpNext';
import { RLearningContentProvider } from '@/store/v2/remote.gen';
import { LearningTypeLabel } from '@/ui/Label';
import { useLearningJourneyBreadcrumbs } from '@/store/v2/journeys/useLearningJourneyBreadcrumbs';
import { useBackToJourneyHref } from '@/features/course/learner/hooks/useBackToJourneyHref';

type SurveyStartProps = ReturnType<UseSurveyViewerHook> & {
  nextItem?: LearningJourneyItem;
  allUpcomingItemsLocked?: boolean;
  allUpcomingItemsUnlockAt?: Date | string;
};

export const SurveyStart = (props: SurveyStartProps): JSX.Element => {
  const { isAdmin, loading } = useCurrentUserAccess();
  const displayAdminButtonGroup = !loading && isAdmin;

  const isLoading = props.loadingLearning || !props.loadingProgressInitialized;

  const learningJourneyBreadcrumbs = useLearningJourneyBreadcrumbs({
    id: Number(props.survey?.id),
  });

  const backToJourneyHref = useBackToJourneyHref({ id: Number(props.survey?.id) });

  if (isLoading || !props.survey) {
    return (
      <StartLayout
        hero={<Hero.LoadingState />}
        main={<StartDescription.LoadingState />}
        sidebar={<StartProgressOverview.LoadingState />}
      />
    );
  }

  return (
    <StartLayout
      headerStart={
        learningJourneyBreadcrumbs ? (
          <Button to={backToJourneyHref} variant="ghost" icon={['fas', 'arrow-left']} $iconPosition="start" size="large">
            Back to journey
          </Button>
        ) : (
          <></>
        )
      }
      headerEnd={
        displayAdminButtonGroup ? (
          <StartAdminButtonGroup course={{ id: Number(props.survey.id), title: props.survey.title }} />
        ) : (
          <></>
        )
      }
      hero={<Hero title={props.survey.title} imageUrl={props.survey.image} provider={RLearningContentProvider.Collegial} />}
      main={
        <>
          <StartDescription headline="Survey description" description={props.survey.teaser} />
          <div>
            <LearningTypeLabel type="SURVEY" text="Survey" />
          </div>
        </>
      }
      sidebar={
        <>
          <StartProgressOverview
            course={{
              effortHours: props.survey.effortHours,
              effortMinutes: props.survey.effortMinutes,
            }}
            progress={props.progress}
            onStart={props.startSurvey}
            startLoading={props.loadingStartCourse}
            onContinue={props.onContinueSurvey}
            onRevisit={props.onRevisitSurvey}
            startedHeadline="Survey progress"
          />
          {!!props.progress?.isCompleted && (!!props.nextItem || props.allUpcomingItemsLocked) && (
            <StartUpNext
              learningCardProps={
                props.nextItem
                  ? {
                      objectId: props.nextItem.contentId,
                      title: props.nextItem.content?.title,
                      imageUrl: props.nextItem.content?.imageURL,
                      type: props.nextItem.content?.metadata?.type,
                      level: props.nextItem.content?.metadata?.level,
                      totalEffort: props.nextItem.content?.plannedDuration,
                    }
                  : undefined
              }
              actionHref={
                props.nextItem?.content?.curations?.[0]?.launchURLPath
                  ? `${props.nextItem.content.curations[0].launchURLPath}`
                  : '#'
              }
              parentJourneyId={learningJourneyBreadcrumbs?.journeyId}
              allUpcomingItemsLocked={props.allUpcomingItemsLocked}
              unlockAt={props.allUpcomingItemsUnlockAt}
            />
          )}
        </>
      }
    />
  );
};
